import { type CSSProperties } from "react";
import type { PromptArgDef, PromptArgType } from "../../../lib/types";

/**
 * Sprint 11 (S11.39) — declarative editor for a prompt's typed arguments. Each
 * row is one argument: name, type, required, description, delete. Writes the
 * whole list back via onChange; the parent (PromptView) persists it through
 * update_prompt. Constrained to 4 types + a flat list — no nesting, no reorder
 * (the form is small in practice).
 */

const ARG_TYPES: PromptArgType[] = ["text", "id", "date", "number"];

interface Props {
  args: PromptArgDef[];
  onChange: (args: PromptArgDef[]) => void;
  disabled?: boolean;
}

export function PromptArgumentsEditor({ args, onChange, disabled }: Props) {
  function update(i: number, patch: Partial<PromptArgDef>) {
    onChange(args.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }
  function remove(i: number) {
    onChange(args.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...args, { name: "", type: "text", required: false }]);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={headerRow}>
        <span style={{ ...colName, ...headCell }}>Name</span>
        <span style={{ ...colType, ...headCell }}>Type</span>
        <span style={{ ...colReq, ...headCell }}>Required</span>
        <span style={{ ...colDesc, ...headCell }}>Description</span>
        <span style={{ ...colDel }} />
      </div>

      {args.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", padding: "4px 2px" }}>
          No arguments. Add one to make the template parameterised, e.g. <code>{"{{enquiry_text}}"}</code>.
        </div>
      ) : (
        args.map((arg, i) => (
          <div key={i} style={argRow}>
            <input
              style={{ ...input, ...colName }}
              value={arg.name}
              disabled={disabled}
              onChange={(e) => update(i, { name: e.target.value.replace(/\s+/g, "_") })}
              placeholder="argument_name"
              aria-label="Argument name"
            />
            <select
              style={{ ...select, ...colType }}
              value={arg.type}
              disabled={disabled}
              onChange={(e) => update(i, { type: e.target.value as PromptArgType })}
              aria-label="Argument type"
            >
              {ARG_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <label style={{ ...colReq, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={arg.required}
                disabled={disabled}
                onChange={(e) => update(i, { required: e.target.checked })}
                aria-label="Required"
              />
            </label>
            <input
              style={{ ...input, ...colDesc }}
              value={arg.description ?? ""}
              disabled={disabled}
              onChange={(e) => update(i, { description: e.target.value })}
              placeholder="Help text shown in the args form"
              aria-label="Argument description"
            />
            <button
              type="button"
              style={{ ...delBtn, ...colDel }}
              disabled={disabled}
              onClick={() => remove(i)}
              aria-label={`Remove ${arg.name || "argument"}`}
            >
              <i className="ti ti-trash" style={{ fontSize: 14 }} aria-hidden="true" />
            </button>
          </div>
        ))
      )}

      <div>
        <button type="button" style={addBtn} disabled={disabled} onClick={add}>
          <i className="ti ti-plus" style={{ fontSize: 13, marginRight: 4 }} aria-hidden="true" />
          Add argument
        </button>
      </div>
    </div>
  );
}

const colName: CSSProperties = { flex: "1 1 140px", minWidth: 0 };
const colType: CSSProperties = { width: 90, flexShrink: 0 };
const colReq: CSSProperties = { width: 70, flexShrink: 0 };
const colDesc: CSSProperties = { flex: "2 1 200px", minWidth: 0 };
const colDel: CSSProperties = { width: 28, flexShrink: 0 };

const headerRow: CSSProperties = { display: "flex", gap: 8, alignItems: "center", padding: "0 2px" };
const headCell: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
const argRow: CSSProperties = { display: "flex", gap: 8, alignItems: "center" };

const input: CSSProperties = {
  fontSize: 13,
  padding: "6px 8px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  boxSizing: "border-box",
};

const select: CSSProperties = {
  ...input,
  cursor: "pointer",
  background: "var(--color-background-secondary)",
};

const delBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 4,
};

const addBtn: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  padding: "6px 10px",
  display: "inline-flex",
  alignItems: "center",
};
